import { ServiceHandler } from '../../../utils/api.utils';

export type UserMemory = {
  profileMd: string;
  tokenCount: number;
  /**
   * Server-side token ceiling. Optional: older backends only expose the
   * budget via the 422 body, so the UI falls back to a default when absent.
   */
  tokenBudget?: number | null;
  version: number;
  enabled: boolean;
  updatedAt: string | null;
};

/**
 * Body of the 422 the PUT /user-memory endpoint returns when the profile
 * exceeds the token budget. The draft is never truncated client-side; we
 * surface `detail` inline and keep the user's text.
 */
export type UserMemoryBudgetError = {
  detail: string;
  tokenCount: number;
  tokenBudget: number;
};

export type DeleteUserMemoryResponse = {
  deleted: true;
};

export const getUserMemory = ServiceHandler<unknown, UserMemory>({
  url: '/api/chat-agent/v1/user-memory',
});

export const updateUserMemory = ServiceHandler<
  { profileMd: string },
  UserMemory
>({
  url: '/api/chat-agent/v1/user-memory',
  method: 'PUT',
});

export const setUserMemoryEnabled = ServiceHandler<
  { enabled: boolean },
  UserMemory
>({
  url: '/api/chat-agent/v1/user-memory/enabled',
  method: 'PUT',
});

export const deleteUserMemory = ServiceHandler<unknown, DeleteUserMemoryResponse>(
  {
    url: '/api/chat-agent/v1/user-memory',
    method: 'DELETE',
  }
);

/**
 * Extracts the token-budget payload from a PUT /user-memory 422 response.
 * Returns null for any other error so callers can fall back to a generic
 * message. Lives here (next to the endpoint) so the 422 contract is owned by
 * the API layer rather than re-parsed in the component.
 */
export function extractBudgetError(err: unknown): UserMemoryBudgetError | null {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (response?.status !== 422) {
    return null;
  }
  const data = response.data as Partial<UserMemoryBudgetError> | undefined;
  if (
    data &&
    typeof data.detail === 'string' &&
    typeof data.tokenBudget === 'number'
  ) {
    return {
      detail: data.detail,
      tokenBudget: data.tokenBudget,
      tokenCount: typeof data.tokenCount === 'number' ? data.tokenCount : 0,
    };
  }
  return null;
}
